/**
 * RevenueAreaChart — 6-month revenue + students trend chart.
 *
 * Expects: data = [{ month: "Jan", revenue: 120, students: 5 }, ...]
 */
import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: "rgba(10,10,10,0.92)",
        backdropFilter: "blur(16px)",
        border: "1px solid color-mix(in srgb, var(--text-primary) 10%, transparent)",
        borderRadius: "10px",
        px: 2,
        py: 1.5,
        minWidth: 120,
      }}
    >
      <Typography
        sx={{
          color: "color-mix(in srgb, var(--text-primary) 50%, transparent)",
          fontSize: "0.68rem",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          letterSpacing: "0.04em",
          mb: 0.8,
        }}
      >
        {label}
      </Typography>
      {payload.map((entry, i) => (
        <Stack key={i} direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.3 }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: entry.color }} />
            <Typography sx={{ fontSize: "0.72rem", color: "color-mix(in srgb, var(--text-primary) 60%, transparent)", fontFamily: "'Inter', sans-serif" }}>
              {entry.name}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>
            {entry.dataKey === "revenue" ? `€${entry.value.toLocaleString()}` : entry.value}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

const RevenueAreaChart = ({ data = [], height = 220, showStudents = true }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) return null;

  const hasRevenue = data.some((d) => d.revenue > 0);
  const hasStudents = showStudents && data.some((d) => d.students > 0);

  if (!hasRevenue && !hasStudents) return null;

  // Calculate totals for summary
  const totalRevenue = data.reduce((sum, d) => sum + (d.revenue || 0), 0);
  const totalStudents = data.reduce((sum, d) => sum + (d.students || 0), 0);
  const currentMonth = data[data.length - 1];
  const previousMonth = data.length > 1 ? data[data.length - 2] : null;

  let revenueTrend = null;
  if (previousMonth && previousMonth.revenue > 0) {
    const pct = ((currentMonth.revenue - previousMonth.revenue) / previousMonth.revenue * 100).toFixed(0);
    revenueTrend = pct > 0 ? `+${pct}%` : `${pct}%`;
  } else if (currentMonth.revenue > 0 && previousMonth?.revenue === 0) {
    revenueTrend = "+100%";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          background: "color-mix(in srgb, var(--text-primary) 2%, transparent)",
          border: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
        }}
      >
        {/* Header with summary */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 600,
                color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
                fontFamily: "'Inter', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 0.5,
              }}
            >
              {t("teacher_dashboard.revenue_6m", "6-Month Revenue")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  fontFamily: "'Space Grotesk', sans-serif",
                  lineHeight: 1,
                }}
              >
                €{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Typography>
              {revenueTrend && (
                <Box
                  sx={{
                    px: 0.8,
                    py: 0.2,
                    borderRadius: "4px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    color: revenueTrend.startsWith("+") ? "#10b981" : "#ef4444",
                    background: revenueTrend.startsWith("+") ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                  }}
                >
                  {revenueTrend}
                </Box>
              )}
            </Stack>
          </Box>
          {hasStudents && (
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
                  fontFamily: "'Inter', sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 0.5,
                }}
              >
                {t("teacher_dashboard.total_enrollments", "Enrollments")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: "color-mix(in srgb, var(--text-primary) 70%, transparent)",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {totalStudents}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Chart */}
        <Box sx={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="studentsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="color-mix(in srgb, var(--text-primary) 6%, transparent)"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "color-mix(in srgb, var(--text-primary) 30%, transparent)",
                  fontSize: 11,
                  fontFamily: "'Inter', sans-serif",
                }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "color-mix(in srgb, var(--text-primary) 25%, transparent)",
                  fontSize: 10,
                  fontFamily: "'Inter', sans-serif",
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              {hasRevenue && (
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name={t("teacher_dashboard.stat_revenue", "Revenue")}
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#revenueGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#10b981", stroke: "var(--mui-palette-background-default)", strokeWidth: 2 }}
                />
              )}
              {hasStudents && (
                <Area
                  type="monotone"
                  dataKey="students"
                  name={t("teacher_dashboard.stat_students", "Students")}
                  stroke="#22d3ee"
                  strokeWidth={2}
                  fill="url(#studentsGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#22d3ee", stroke: "var(--mui-palette-background-default)", strokeWidth: 2 }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </motion.div>
  );
};

export default RevenueAreaChart;
